import React, { useMemo } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { DollarSign, Activity, AlertTriangle, Clock } from 'lucide-react-native';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useLlmUsageDaily, summarizeLlmUsage } from '@/lib/admin/use-llm-usage';

const fmtUsd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;

export default function AICostScreen() {
  const colors = useThemeColors();
  const { data: rows, isLoading, isError } = useLlmUsageDaily(30);
  const s = useMemo(() => summarizeLlmUsage(rows ?? []), [rows]);

  const stat = (icon: React.ReactNode, label: string, value: string, tint?: string) => (
    <View
      className="flex-1 rounded-2xl border p-3"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light, minWidth: 150 }}
    >
      <View className="flex-row items-center gap-2">{icon}
        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{label}</Text>
      </View>
      <Text style={{ color: tint ?? colors.text.primary }} className="text-xl font-bold mt-1" numberOfLines={1}>{value}</Text>
    </View>
  );

  return (
    <AppLayout
      title="AI Cost & Health"
      breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'AI Cost & Health' }]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={isWeb ? [] : ['top', 'bottom']}>
        <View className="px-1 pt-2 pb-3">
          <View className="flex-row items-center">
            <DollarSign size={18} color={colors.text.primary} />
            <Text style={{ color: colors.text.primary }} className="text-xl font-bold ml-2">AI Cost &amp; Health</Text>
          </View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
            Per-call LLM telemetry, last 30 days. Best-effort rollup of every model call (cost is an estimate).
          </Text>
        </View>

        {isLoading ? (
          <View className="flex-1 items-center justify-center py-12"><ActivityIndicator color={colors.primary[600]} /></View>
        ) : isError ? (
          <Text style={{ color: colors.traffic.red }} className="text-sm px-1">Couldn’t load LLM usage.</Text>
        ) : s.totalCalls === 0 ? (
          <Text style={{ color: colors.text.tertiary }} className="text-sm px-1">
            No LLM usage recorded yet. Rows appear here as production model calls run.
          </Text>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
            {/* Summary stats */}
            <View className="flex-row flex-wrap gap-2">
              {stat(<DollarSign size={14} color={colors.primary[600]} />, 'Cost (30d)', fmtUsd(s.totalCostUsd))}
              {stat(<Activity size={14} color={colors.primary[600]} />, 'Calls', s.totalCalls.toLocaleString())}
              {stat(
                <AlertTriangle size={14} color={s.errorRate > 0.05 ? colors.traffic.red : colors.text.tertiary} />,
                'Error rate', `${(s.errorRate * 100).toFixed(1)}%`,
                s.errorRate > 0.05 ? colors.traffic.red : undefined,
              )}
              {stat(<Clock size={14} color={colors.primary[600]} />, 'Avg latency', `${Math.round(s.avgLatencyMs)}ms`)}
            </View>

            {/* By model */}
            <Text style={{ color: colors.text.primary }} className="text-base font-semibold mt-5 mb-2">Cost by model</Text>
            <View className="rounded-2xl border overflow-hidden" style={{ borderColor: colors.border.light }}>
              {s.byModel.map((m, i) => (
                <View
                  key={`${m.provider}-${m.model}`}
                  className="flex-row items-center justify-between px-3 py-2.5"
                  style={{ backgroundColor: colors.surface.primary, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.border.light }}
                >
                  <View className="flex-1 pr-2">
                    <Text style={{ color: colors.text.primary }} className="text-sm font-medium" numberOfLines={1}>{m.model}</Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{m.provider} · {m.calls.toLocaleString()} calls</Text>
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-sm font-semibold">{fmtUsd(m.costUsd)}</Text>
                </View>
              ))}
            </View>

            {/* Daily trend */}
            <Text style={{ color: colors.text.primary }} className="text-base font-semibold mt-5 mb-2">Daily cost</Text>
            <View className="rounded-2xl border overflow-hidden" style={{ borderColor: colors.border.light }}>
              {[...s.byDay].reverse().map((d, i) => (
                <View
                  key={d.day}
                  className="flex-row items-center justify-between px-3 py-2"
                  style={{ backgroundColor: colors.surface.primary, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.border.light }}
                >
                  <Text style={{ color: colors.text.secondary }} className="text-sm">{d.day}</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs flex-1 text-right mr-3">{d.calls.toLocaleString()} calls</Text>
                  <Text style={{ color: colors.text.primary }} className="text-sm font-medium">{fmtUsd(d.costUsd)}</Text>
                </View>
              ))}
            </View>
          </ScrollView>
        )}
      </SafeAreaView>
    </AppLayout>
  );
}
